import { ExternalLink } from 'lucide-react';
import { useRows } from '@/hooks/useResource';
import { signedUrlsFor } from '@/api/documents';
import { formatDateTime } from '@/components/live';

interface LinkedDocument { link_id: string; link_type: string; linked_at: string; document_id: string; original_filename: string | null }

/**
 * The latest document of one kind linked to a record - a consent's draft or signed copy, a sale's
 * draft or final invoice - as a link that opens it (owner, 2026-09-27: "any where can i see a draft
 * and consented file?"). Nothing when there is none.
 */
export function LinkedDocumentLink({ entityTable, entityId, linkType, label }: {
  entityTable: string; entityId: string; linkType: 'generated' | 'primary'; label: string;
}) {
  const docs = useRows<LinkedDocument>('record_documents', {
    filters: { entity_table: entityTable, entity_id: entityId, link_type: linkType },
    orderBy: { column: 'linked_at', ascending: false },
  });
  // A consent's draft and its print copy are both 'generated': the draft is the one that is not "-print".
  const latest = (docs.data ?? []).find((row) => !/-print\.pdf$/.test(row.original_filename ?? ''));
  if (!latest) return <span className="muted">--</span>;
  return (
    <button className="link-button" title={`${latest.original_filename ?? ''} · ${formatDateTime(latest.linked_at)}`} type="button"
            onClick={(event) => {
              event.stopPropagation();
              void signedUrlsFor([latest.document_id]).then((urls) => { const url = urls[latest.document_id]; if (url) window.open(url, '_blank', 'noopener'); });
            }}>
      <ExternalLink size={13} /> {label}
    </button>
  );
}

/** Every document linked to a record, newest first, each one to open. */
export function LinkedDocumentList({ entityTable, entityId, labels }: {
  entityTable: string; entityId: string; labels: Record<string, string>;
}) {
  const docs = useRows<LinkedDocument>('record_documents', {
    filters: { entity_table: entityTable, entity_id: entityId },
    orderBy: { column: 'linked_at', ascending: false },
  });
  if (!docs.data?.length) return null;
  return (
    <ul className="child-record-list">
      {docs.data.map((row) => (
        <li key={row.link_id}>
          <span className="child-record-text"><strong>{labels[row.link_type] ?? row.link_type}</strong>
            <small className="muted">{row.original_filename ?? ''} · {formatDateTime(row.linked_at)}</small></span>
          <button onClick={() => void signedUrlsFor([row.document_id]).then((urls) => { const url = urls[row.document_id]; if (url) window.open(url, '_blank', 'noopener'); })} type="button">
            <ExternalLink size={14} /> Open
          </button>
        </li>
      ))}
    </ul>
  );
}
